import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {change} from 'redux-form';

import type {AddButtonPropsSingular} from 'shells/card/card-widget/card-widget';

import type {WidgetFragment} from 'nutshell-graphql-types';

import {LEADS_FORM_KEY} from '../../../entity-form/forms/lead-form-connected';

import {appHistory} from '../../../../history';

import {useGetPeepRelatedLeads} from './graphql/hooks/use-get-peep-related-leads';
import {getLeadsListLink} from './helpers';
import {LeadsWidgetContent} from './leads-widget-content';

type Props = {
    peepId: string;
    widget: WidgetFragment;
};

export type WidgetLeadStatus = 'Open' | 'Won' | 'Lost' | 'Cancelled';

export function PeepLeadsWidget(props: Props) {
    const dispatch = useDispatch();
    const {leads, stats, peep, isLoading} = useGetPeepRelatedLeads(props.peepId);

    const handleCreateNewLead = useCallback(() => {
        // Let's make sure our form is instantiated
        appHistory.push({hash: '/create/leads', state: {modal: true}});

        if (!peep) {
            return;
        }

        let tempPeep = null;

        // Pass rich data up to the lead create modal
        if (peep.__typename === 'Contact') {
            tempPeep = {
                id: peep.id,
                type: peep.type,
                value: peep.name,
                primaryPhoneText: peep.phones?.primary?.value?.numberFormatted,
                primaryEmailText: peep.emailAddresses?.primary?.value?.display,
                jobTitle: peep.jobTitle,
                avatarUrl: peep.avatarUrl,
                entityDescription: peep.description,
            };
        } else {
            tempPeep = {
                id: peep.id,
                type: peep.type,
                value: peep.name,
                description: peep.description,
                tertiaryInfo: peep.postalAddresses?.primary?.value?.region,
                avatarUrl: peep.avatarUrl,
            };
        }
        setTimeout(() => {
            dispatch(change(LEADS_FORM_KEY, `links.${peep.type}`, [tempPeep]));
        }, 500);
    }, [peep, dispatch]);

    const addButtonProps: AddButtonPropsSingular = useMemo(
        () => ({
            variant: 'singular',
            label: '+ Create a lead',
            onClick: handleCreateNewLead,
        }),
        [handleCreateNewLead]
    );

    const getSeeAllLink = useCallback(
        (tab: WidgetLeadStatus) => getLeadsListLink(props.peepId, peep?.__typename, tab),
        [peep, props.peepId]
    );

    return (
        <LeadsWidgetContent
            widget={props.widget}
            leads={leads}
            stats={stats}
            isLoading={isLoading}
            addButtonProps={addButtonProps}
            getSeeAllLink={getSeeAllLink}
        />
    );
}
